import { useEffect, useRef, useState } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  PriceFormType,
  ResponsePriceType,
} from '../../redux/types/pricesTypes';
import { ButtonType, TabsNames } from '../../utils/@globalTypes';
import usePriceValidationSchema from './priceValidationSchema';
import Button from '../Button';
import { RoutesList } from '../../App';
import { updatePrices } from '../../redux/reducers/pricesSlice';
import styles from './RentalPriceSettings.module.scss';
import SettingFormInner from './SettingFormInner';
import ScrollToError from '../Booking/ScrollToError';
import {
  comparePrices,
  createInitialKidsPrice,
  isPricesEqual,
} from '../../utils/comparePrices';
import { RentalObjectSelectors } from '../../redux/reducers/rentalObjectSlice';

type PriceSettings = {
  prices: ResponsePriceType | null;
};

const RentalPriceSettings = ({ prices }: PriceSettings) => {
  const { mainId } = useParams();
  const dispatch = useDispatch();
  const validationSchema = usePriceValidationSchema();

  const currentRentalObject = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );
  const max_places = currentRentalObject?.max_places || null;

  const [commonError, setCommonError] = useState<string>('');
  const [isChanged, setIsChanged] = useState(false);

  const initialAnimal = prices?.animal_price
    ? prices?.animal_price.toString()
    : '';

  const initialMinGuestsWeekday = prices?.extra_guest_price?.min_guests_weekday
    ? prices?.extra_guest_price?.min_guests_weekday.toString()
    : '1';

  const initialMinGuestsWeekend = prices?.extra_guest_price?.min_guests_weekend
    ? prices?.extra_guest_price?.min_guests_weekend.toString()
    : '1';

  const initialAgeKidsFree = prices?.extra_guest_price?.age_kids_free
    ? prices?.extra_guest_price?.age_kids_free.toString()
    : '';

  const initialKidsPrices = prices?.kids_prices
    ? createInitialKidsPrice(prices?.kids_prices)
    : [];

  const initialValues: PriceFormType = {
    base_price: {
      high_season_weekday_price:
        prices?.base_price?.high_season_weekday_price || '',
      high_season_weekend_price:
        prices?.base_price?.high_season_weekend_price || '',
      low_season_weekday_price:
        prices?.base_price?.low_season_weekday_price || '',
      low_season_weekend_price:
        prices?.base_price?.low_season_weekend_price || '',
    },
    max_places: max_places,
    extra_guest_price: {
      high_season_weekday_price:
        prices?.extra_guest_price?.high_season_weekday_price || '',
      high_season_weekend_price:
        prices?.extra_guest_price?.high_season_weekend_price || '',
      low_season_weekday_price:
        prices?.extra_guest_price?.low_season_weekday_price || '',
      low_season_weekend_price:
        prices?.extra_guest_price?.low_season_weekend_price || '',
      min_guests_weekday: initialMinGuestsWeekday,
      min_guests_weekend: initialMinGuestsWeekend,
    },
    isPriceDynamic: prices?.is_dynamic || false,
    start_date_month: prices?.base_price?.start_date?.slice(0, 2) || '',
    start_date_day: prices?.base_price?.start_date?.slice(3) || '',
    end_date_month: prices?.base_price?.end_date?.slice(0, 2) || '',
    end_date_day: prices?.base_price?.end_date?.slice(3) || '',
    has_kidsPrice: prices?.extra_guest_price?.is_kids_price_active || false,
    isAnimalPrice: prices?.is_animal_price_active || false,
    ageKidsFree: initialAgeKidsFree,
    animalPrice: initialAnimal,
    kidsPrice: initialKidsPrices,
  };
  const formikRef = useRef<FormikProps<PriceFormType>>(null);

  return (
    <Formik<PriceFormType>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={true}
      validateOnMount={false}
      enableReinitialize={true}
      innerRef={formikRef}
      onSubmit={(values, formikBag) => {
        const updatePrice = prices ? comparePrices(values, prices) : {};
        const { resetForm, setSubmitting } = formikBag;
        try {
          if (            
            Object.keys(updatePrice).length>0 &&
            prices?.id &&
            !commonError
          ) {
            dispatch(
              updatePrices({
                id: prices.id,
                data: updatePrice,
              }),
            );
          } else {
            resetForm()
            setSubmitting(false);
          }
        } catch (error) {
          console.error('Ошибка отправки:', error);
        }
      }}
    >
      {(formik) => {
        useEffect(() => {
          // Сравниваем текущие значения с начальными
          if (isPricesEqual(formik.values, initialValues)) {
            setIsChanged(false);
          } else {
            setIsChanged(true);
          }
        }, [formik.values, initialValues]);

        return (
          <Form>
            <div className={styles.currency}>
              <div className={styles.currencyTitle}>Цены указываются в BYN</div>
            </div>

            <SettingFormInner
              formik={formik}
              commonError={commonError}
              setCommonError={setCommonError}
            />

            <div className={styles.mainBtnsWrapper}>
              <Button
                title="Сохранить"
                type={ButtonType.PRIMARY_SMALL}
                actionType="submit"
                disabled={!isChanged || !formik.isValid || !formik.dirty}
                className={styles.mainBtn}
              />
              <Button
                title={false ? 'Отмена' : 'Назад'}
                type={ButtonType.SECONDARY_SMALL}
                path={`${RoutesList.objectManagement.objectEditingChild.fullPath}/${mainId}/${TabsNames.OBJECT_EDITING}`}
                className={styles.mainBtn}
              />
            </div>
            <ScrollToError />
          </Form>
        );
      }}
    </Formik>
  );
};

export default RentalPriceSettings;
